import { useEffect, useId, useState } from 'react';
import type { FC } from 'react';

import { useCms, useContent } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import { DownloadContractTile } from '@eneco/components/DownloadContractTile/DownloadContractTile';
import { useDocumentsDownloadDocumentByDocumentId, useDocumentsGetCustomerContracts } from '@eneco/dc/hooks';
import { Heading, NotificationBox, Stack, TextLink, TileBar, useToast } from '@eneco/sparky';
import type { NotificationBoxProps } from '@eneco/sparky/types';

import { findByRole } from './AddressFinder';
import type { DownloadContractsContent } from './download-contracts-content.types';

/**
 * `DownloadContracts` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy Sitecore lane (`libs/sitecore/components/content/src/DownloadContracts/
 * DownloadContracts.tsx`): a list of the customer's active contracts (`useDocumentsGetCustomerContracts`,
 * DC-backed) as downloadable tiles, with a link below. Renders nothing when there are no contracts and
 * the fetch itself did not fail.
 *
 * ADR-0068's role-collapse turned the legacy per-field notifications into role-tagged entries of one
 * `notifications` array — see `download-contracts-content.types.ts`.
 *
 * Known simplification: drops the legacy component's `useTracking().trackError` call
 * (`@eneco/tracking`) on a failed download — `lib:tracking` is not in `lib:cms-components`'s allowed
 * dependencies, the same class of trade `NotificationBox.tsx` documents for dropping its view tracking.
 * The error toast itself is unaffected, only that tracking signal is lost.
 */
const DownloadContracts: FC = () => {
  const { adapter } = useCms();
  const content = useContent<DownloadContractsContent>();
  const { showToast } = useToast();
  const id = useId();
  const [documentId, setDocumentId] = useState('');

  const errorNotification = findByRole(content.notifications, 'errorNotification') ?? content.errorNotification;
  const fetchErrorNotification = findByRole(content.notifications, 'fetchErrorNotification') ?? content.fetchErrorNotification;

  const { data, error: fetchError } = useDocumentsGetCustomerContracts({ includeActiveContracts: true });
  const contracts = data?.contracts ?? [];

  const { isValidating, error: downloadContractError } = useDocumentsDownloadDocumentByDocumentId(
    documentId.length > 0 ? { documentId } : null,
    { errorRetryCount: 0 },
  );

  const downloadContract = (contractId: string) => {
    setDocumentId(contractId);
  };

  useEffect(() => {
    if (!downloadContractError) return;
    showToast({
      title: errorNotification?.title,
      text: adapter.toRichText(errorNotification?.content),
      variant: (errorNotification?.variant as NotificationBoxProps['variant']) ?? 'error',
      isAlert: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadContractError]);

  const link = linkWithUrl(content.link);

  if (!fetchError && contracts.length === 0) return null;

  return (
    <Stack direction="column" gap="10">
      <Stack direction="column" gap="6">
        <Heading size="2XS" as="h3" id={id}>
          {content.title}
        </Heading>
        {fetchError ? (
          <NotificationBox
            title={fetchErrorNotification?.title}
            text={adapter.toRichText(fetchErrorNotification?.content)}
            variant={(fetchErrorNotification?.variant as NotificationBoxProps['variant']) ?? 'error'}
            isAlert={false}
          />
        ) : (
          <nav aria-labelledby={id}>
            <TileBar direction="column">
              {contracts.map(contract => (
                <DownloadContractTile
                  productTypes={contract.productTypes}
                  documentId={contract.contractId}
                  onDownloadContract={downloadContract}
                  key={contract.contractId}
                  isValidating={isValidating && documentId === contract.contractId}
                  contractState={contract.contractState}
                />
              ))}
            </TileBar>
          </nav>
        )}
      </Stack>
      {link?.href && (
        <TextLink emphasis="high" href={link.href} target={link.target}>
          {link.text ?? link.href}
        </TextLink>
      )}
    </Stack>
  );
};

export default DownloadContracts;
